import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Col, Grid, Modal, Row } from 'rsuite';

import { choices } from '../../../constants/choices';
import { FilterModal } from '../../../containers/styled/alerts';
import { TertiaryButton } from '../../../containers/styled/buttons';
import {
  DateField,
  SelectPickerField
} from '../../../containers/styled/styled';
import { Label } from '../../../containers/styled/typography';
import { useDatabase } from '../../../context/database';
import { listFacility } from '../../../controllers/reports/CommonController';
import { parseDate } from '../../../utils/dates';
import { SelectDiv } from '../../generic/styled';

const messages = defineMessages({
  facilityPlaceholder: {
    id: 'report.filter.adverseEvent.facility.placeholder',
    defaultMessage: 'Select Facility'
  },
  severityPlaceholder: {
    id: 'report.filter.adverseEvent.severity.placeholder',
    defaultMessage: 'Select Severity'
  },
  datePeriodPlaceholder: {
    id: 'report.filter.adverseEvent.datePeriod.placeholder',
    defaultMessage: 'Select Date'
  }
});

/**
 * Adverse Event Filter screen
 *
 * Adverse event filter to display modal to filter data table.
 *
 * @component
 * @param {array} data              an array of objects representing the data to be displayed
 * @param {boolean} handleClose     handles modal close event
 * @param setFilteredData           sets filtered data to the data table
 * @param initialQuery              default data to the data table
 * @param setInitialQuery           sets initialQuery data to table before modal closes
 * @returns adverse event view modal.
 *
 */
const AdverseEventFilter = ({ data, handleClose, setFilteredData, initialQuery, setInitialQuery }) => {
  const { formatMessage } = useIntl();
  const db = useDatabase();

  const [facilityData, setFacilityData] = React.useState([]);
  const [query, setQuery] = React.useState({
    facility: '',
    severity: '',
    datePeriod: []
  });

  const filterTable = () => {
    const filteredData = data.filter(
      (entry) =>
        (!query.facility || entry.facility === query.facility) &&
        (!query.severity || entry.severity === query.severity) &&
        (query.datePeriod.length === 0 || (
          entry.dateOfTransfusion >= parseDate(query.datePeriod[0]) &&
          entry.dateOfTransfusion <= parseDate(query.datePeriod[1]))
        )
    );
    setFilteredData(filteredData);
    setInitialQuery(query);
    handleClose();
  };

  React.useEffect(() => {
    listFacility(db).then((data) => {
      const docs = data
        .filter((entry) => entry.isActive) // filter out inactive entries
        .map((entry) => ({
          value: entry.name,
          label: entry.name
        }));
      setFacilityData(docs);
    });
  }, [db]);

  React.useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  return (
    <FilterModal onClose={handleClose} size='xs'>
      <Modal.Body style={{ marginLeft: 26, marginRight: 26 }}>
        <Grid fluid>
          <Row>
            <Col xs={24}>
              <Label>
                <FormattedMessage
                  id='report.filter.adverseEvent.facility'
                  defaultMessage='Facility'
                />
              </Label>
              <SelectDiv>
                <SelectPickerField
                  name='facility'
                  value={query.facility}
                  searchable={false}
                  className='select-md'
                  placeholder={formatMessage(messages.facilityPlaceholder)}
                  data={facilityData}
                  onChange={(value) =>
                    setQuery((state) => ({ ...state, facility: value }))}
                />
              </SelectDiv>
            </Col>
            <Col xs={24}>
              <Label>
                <FormattedMessage
                  id='report.filter.adverseEvent.severity'
                  defaultMessage='Severity'
                />
              </Label>
              <SelectDiv>
                <SelectPickerField
                  name='severity'
                  value={query.severity}
                  searchable={false}
                  className='select-md'
                  placeholder={formatMessage(messages.severityPlaceholder)}
                  data={choices.priority.map((data) => ({
                    label: data.name.defaultMessage,
                    value: data.id
                  }))}
                  onChange={(value) =>
                    setQuery((state) => ({ ...state, severity: value }))}
                />
              </SelectDiv>
            </Col>
            <Col xs={24}>
              <Label>
                <FormattedMessage
                  id='report.filter.adverseEvent.datePeriod'
                  defaultMessage='Date Period'
                />
              </Label>
              <SelectDiv>
                <DateField
                  name='datePeriod'
                  value={query.datePeriod.length ? query.datePeriod : null}
                  className='select-md'
                  block
                  placeholder={formatMessage(messages.datePeriodPlaceholder)}
                  onClean={() => setQuery((state) => ({ ...state, datePeriod: [] }))}
                  onOk={(value) =>
                    setQuery((state) => ({ ...state, datePeriod: value }))}
                />
              </SelectDiv>
            </Col>
            <Col style={{ marginTop: 24 }} xs={24}>
              <TertiaryButton data-testid='filter-button' onClick={() => filterTable()}>
                <FormattedMessage
                  id='report.filter.button'
                  defaultMessage='Filter'
                />
              </TertiaryButton>
            </Col>
          </Row>
        </Grid>
      </Modal.Body>
    </FilterModal>
  );
};

export default AdverseEventFilter;
